import { Metadata } from "grpc-web";
import { useCallback, useMemo } from "react";
import {
  KeycloakRPTSingleton,
  KeycloakSingleton,
  useKeycloak,
} from "../components/keycloak-provider";
import { useProperty, usePropertyOptional } from "../components/property-provider";
import { getPropertyFromRptToken, IRptToken } from "./auth";
import { useAuthorizationService } from "./auth.service";

export interface GrpcClientClass<C> {
  new (
    hostname: string,
    credentials?: null | { [index: string]: string },
    options?: null | { [index: string]: string }
  ): C;
}

export type GrpcCall<C, Req, Res> = (this: C, request: Req, metadata?: Metadata) => Promise<Res>;

//Used for debugging grpc locally
//@ts-ignore
const enableDevTools = window.__GRPCWEB_DEVTOOLS__ || (() => {});

export function buildGrpcClient<C>(clientClass: GrpcClientClass<C>): C {
  //If we need to later on change the hostname, we could do it here.
  //Current design is to route everything through the same host, so no need for this.
  const client = new clientClass("");
  enableDevTools([client]);
  return client;
}

export function addAuthzMetadataGrpcCall<C, Req, Res>(client: C, call: GrpcCall<C, Req, Res>) {
  return (req: Req, metadata?: Metadata) => {
    const accessToken = KeycloakRPTSingleton.rpt ?? (KeycloakSingleton?.token as string);
    const propertyTicker = getPropertyFromRptToken(accessToken);
    const finalMeta = {
      ...(metadata ? metadata : {}),
      ...(propertyTicker ? { "x-property": propertyTicker } : {}),
      Authorization: `Bearer ${accessToken}`,
    };
    return call.apply(client, [req, finalMeta]);
  };
}

/**
 * Creates a new grpc client. The client will not be authenticated.
 * @param clientClass
 */
export function useGrpcClient<C>(clientClass: GrpcClientClass<C>): C {
  return useMemo(() => buildGrpcClient(clientClass), [clientClass]);
}

/**
 * Wraps a GRPC call with a property RPT. This means the call will be authenticated with the current
 * property's RPT.
 * This will error if there is no property RPT available.
 * @param client The client.
 * @param call The call to wrap.
 * @param decodeToken
 */

export function usePropertyGrpcCall<C, Req, Res>(
  client: C,
  call: GrpcCall<C, Req, Res>,
  decodeToken?: IRptToken
): GrpcCall<void, Req, Res> {
  const property = useProperty();
  return useCallback(
    (req: Req, metadata?: Metadata) => {
      const finalMeta = {
        ...(metadata ? metadata : {}),
        "x-principal": decodeToken ? decodeToken.email : "",
        "x-property": property.ticker,
        Authorization: `Bearer ${property.token}`,
      };
      return call.apply(client, [req, finalMeta]);
    },
    [decodeToken, property.ticker, property.token, call, client]
  );
}

export type PropertyScopedGrpcCall<Req, Res> = (
  propertyTicker: string | undefined,
  req: Req,
  metadata?: Metadata
) => Promise<Res>;

/**
 * Wraps a GRPC call so the caller passes the target property ticker explicitly, and the RPT
 * used to authenticate is always resolved for that same ticker — never the ambient property.
 * Falls back to the ambient property's ticker/token when none is passed.
 * @param client The client.
 * @param call The call to wrap.
 */
export function usePropertyScopedGrpcCall<C, Req, Res>(
  client: C,
  call: GrpcCall<C, Req, Res>
): PropertyScopedGrpcCall<Req, Res> {
  const property = useProperty();
  const authService = useAuthorizationService();
  return useCallback(
    async (propertyTicker, req, metadata) => {
      const ticker = propertyTicker ?? property.ticker;
      const token =
        ticker === property.ticker ? property.token : await authService.getRptToken(ticker);
      return call.apply(client, [
        req,
        { ...metadata, "x-property": ticker, Authorization: `Bearer ${token}` },
      ]);
    },
    [client, call, property, authService]
  );
}

export function useLoyaltyGrpcCall<C, Req, Res>(
  client: C,
  call: GrpcCall<C, Req, Res>,
  decodeToken?: IRptToken
): GrpcCall<void, Req, Res> {
  const property = useProperty();
  return useCallback(
    (req: Req, metadata?: Metadata) => {
      const finalMeta = {
        ...(metadata ? metadata : {}),
        "x-principal": decodeToken ? decodeToken.email : "",
        Authorization: `Bearer ${property.token}`,
      };
      return call.apply(client, [req, finalMeta]);
    },
    [decodeToken, property.token, call, client]
  );
}

export function useInternalGrpcCall<C, Req, Res>(
  client: C,
  call: GrpcCall<C, Req, Res>,
  decodeToken?: IRptToken
): GrpcCall<void, Req, Res> {
  return useCallback(
    (req: Req, metadata?: Metadata) => {
      const accessToken = KeycloakSingleton?.token;
      const finalMeta = {
        ...(metadata ? metadata : {}),
        "x-principal": decodeToken ? decodeToken.email : "",
        Authorization: `Bearer ${accessToken}`,
      };
      return call.apply(client, [req, finalMeta]);
    },
    [decodeToken, call, client]
  );
}

export function usePropertyOptionalGrpcCall<C, Req, Res>(
  client: C,
  call: GrpcCall<C, Req, Res>
): GrpcCall<void, Req, Res> | undefined {
  const property = usePropertyOptional();

  //We can't put this in an if because hook call cannot be conditional. However, it can go unused.
  const result = useCallback(
    (req: Req, metadata?: Metadata) => {
      const finalMeta = {
        ...(metadata ? metadata : {}),
        Authorization: `Bearer ${property!.token}`,
        "x-property": property!.ticker,
      };
      return call.apply(client, [req, finalMeta]);
    },
    [property, client, call]
  );

  return property ? result : undefined;
}

/**
 * Wraps a GRPC call with the user's access token. This means the call will be authenticated with
 * the current user's token.
 *
 * This will error if the user is not authenticated.
 * @param client the client.
 * @param call The call to wrap.
 */
export function useUserGrpcCall<Req, Res, C>(
  client: C,
  call: GrpcCall<C, Req, Res>
): GrpcCall<void, Req, Res> {
  const keycloak = useKeycloak();

  return useCallback(
    (req: Req, metadata?: Metadata) => {
      const finalMeta = {
        ...(metadata ? metadata : {}),
        Authorization: `Bearer ${keycloak.token}`,
      };

      return call.apply(client, [req, finalMeta]);
    },
    [keycloak.token, client, call]
  );
}
